import React, { useEffect, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaClipboardList } from 'react-icons/fa';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import './TermExamsPage.css';

const getAuthToken = () => localStorage.getItem('authToken');

const TermExamsPage = () => {
  const { selectedAcademicYear } = useAppContext();
  const navigate = useNavigate();
  const [termExams, setTermExams] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    batch_id: '',
    subjects: [],
    evaluation_type: 'mark',
  });
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchTermExams = async () => {
    setLoading(true);
    const token = getAuthToken();

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      console.error('No academic year selected');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(
        `${appConstants.API_ROOT_URL}get-term-exams-v2`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );
      setTermExams(res.data.data || []);
    } catch (err) {
      console.error('Error fetching term exams', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    const token = getAuthToken();
    try {
      const res = await axios.get(`${appConstants.API_ROOT_URL}get-batches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBatches(res.data.data || []);
    } catch (err) {
      console.error('Error fetching batches', err);
    }
  };

  const fetchSubjects = async () => {
    const token = getAuthToken();
    try {
      const res = await axios.get(`${appConstants.API_ROOT_URL}get-subjects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(res.data.data || []);
    } catch (err) {
      console.error('Error fetching subjects', err);
    }
  };

  useEffect(() => {
    fetchTermExams();
    fetchBatches();
    fetchSubjects();
    // eslint-disable-next-line
  }, [selectedAcademicYear]);

  const openModal = (data = null) => {
    setEditData(data);

    if (data) {
      // Extract evaluation_type from first subject (assuming all subjects have same evaluation type)
      const firstSubject =
        data.subjects && data.subjects.length > 0 ? data.subjects[0] : null;

      setFormData({
        name: data.name,
        batch_id: data.batch_id,
        subjects: data.subjects.map((s) => s.id), // Extract just IDs
        evaluation_type: firstSubject?.evaluation_type || 'mark',
      });
    } else {
      setFormData({
        name: '',
        batch_id: '',
        subjects: [],
        evaluation_type: 'mark',
      });
    }

    setShowModal(true);
  };

  const handleDelete = async (id, name) => {
    const confirmation = window.confirm(
      `Are you sure you want to delete the term exam "${name}"?\n\n⚠️ This will permanently delete all its marks and subject data. This action cannot be undone.`,
    );

    if (!confirmation) return;

    setSaving(true);
    const token = getAuthToken();

    try {
      const res = await axios.post(
        `${appConstants.API_ROOT_URL}delete-term-exam`,
        { term_exam_id: id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.status === 'success') {
        fetchTermExams();
      } else {
        alert(res.data.message || 'Failed to delete term exam.');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting term exam. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    const token = getAuthToken();

    const url = editData
      ? `${appConstants.API_ROOT_URL}update-term-exam-v2`
      : `${appConstants.API_ROOT_URL}create-term-exam-v2`;

    // Build subjects array with evaluation_type
    const subjectsPayload = formData.subjects.map((subjectId) => ({
      subject_id: parseInt(subjectId),
      evaluation_type: formData.evaluation_type,
    }));

    const payload = {
      name: formData.name,
      batch_id: formData.batch_id,
      subjects: subjectsPayload,
    };

    if (editData) {
      payload.term_exam_id = editData.id;
    }

    try {
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.status === 'success') {
        fetchTermExams();
        setShowModal(false);
      } else {
        alert(res.data.message || 'Something went wrong.');
      }
    } catch (err) {
      console.error('Submit error:', err);

      if (err.response && err.response.data) {
        // Backend error response
        const message =
          err.response.data.message || 'Server responded with an error.';
        alert(message);
      } else if (err.request) {
        // No response received
        alert(
          'No response from server. Please check your internet connection.',
        );
      } else {
        // Any other error
        alert('Unexpected error occurred. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const openExamMarksPage = (exam) => {
    setSelectedExam(exam);
    setSelectedSubjectId(''); // Reset subject selection
    setShowSubjectModal(true);
  };

  return (
    <Layout>
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Top Bar */}
          <div
            style={{
              background: '#343a40',
              color: '#fff',
              borderRadius: 8,
              padding: '0.7rem 1rem 0.7rem 1rem',
              marginBottom: 20,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <FaClipboardList />
                Term Exams
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  marginTop: 4,
                  opacity: 0.9,
                }}
              >
                Manage term exams, subjects, and evaluation types
              </div>
            </div>
            <Button
              onClick={() => openModal()}
              variant="light"
              size="sm"
              style={{
                borderRadius: 6,
                padding: '8px 16px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FaPlus />
              Create Exam
            </Button>
          </div>

          {/* Table Section */}
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <table className="table table-hover mb-0">
              <thead
                style={{
                  background: '#f8f9fa',
                  borderBottom: '2px solid #e9ecef',
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: '16px 20px',
                      fontWeight: '600',
                      color: '#495057',
                    }}
                  >
                    Exam Name
                  </th>
                  <th
                    style={{
                      padding: '16px 20px',
                      fontWeight: '600',
                      color: '#495057',
                    }}
                  >
                    Batch
                  </th>
                  <th
                    style={{
                      padding: '16px 20px',
                      fontWeight: '600',
                      color: '#495057',
                      width: '400px',
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {termExams.length === 0 ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="text-center py-5"
                      style={{ color: '#6c757d' }}
                    >
                      <FaClipboardList
                        size={48}
                        style={{ opacity: 0.3, marginBottom: '16px' }}
                      />
                      <p className="mb-0">
                        No term exams created yet. Click "Create Term Exam" to
                        get started.
                      </p>
                    </td>
                  </tr>
                ) : (
                  termExams.map((term) => (
                    <tr
                      key={term.id}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <td
                        style={{
                          padding: '16px 20px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: '600',
                            color: '#2c3e50',
                            fontSize: '0.95rem',
                          }}
                        >
                          {term.name}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '16px 20px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <span
                          style={{
                            background:
                              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#fff',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                          }}
                        >
                          {term.batch_name}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '16px 20px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Button
                            size="sm"
                            onClick={() => openModal(term)}
                            style={{
                              background: '#ffc107',
                              color: '#000',
                              border: 'none',
                              padding: '6px 14px',
                              borderRadius: '6px',
                              fontWeight: '600',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow =
                                '0 4px 8px rgba(255, 193, 7, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            <FaEdit className="me-1" /> Edit
                          </Button>

                          <Button
                            size="sm"
                            onClick={() => openExamMarksPage(term)}
                            style={{
                              background: '#17a2b8',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 14px',
                              borderRadius: '6px',
                              fontWeight: '600',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow =
                                '0 4px 8px rgba(23, 162, 184, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            Enter Marks
                          </Button>

                          <Button
                            size="sm"
                            onClick={() =>
                              navigate(
                                `/term-exam-marks-view?term_exam_id=${term.id}&batch_id=${term.batch_id}`,
                              )
                            }
                            style={{
                              background: '#6c757d',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 14px',
                              borderRadius: '6px',
                              fontWeight: '600',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow =
                                '0 4px 8px rgba(108, 117, 125, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            View
                          </Button>

                          <Button
                            size="sm"
                            onClick={() => handleDelete(term.id, term.name)}
                            disabled={saving}
                            style={{
                              background: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 14px',
                              borderRadius: '6px',
                              fontWeight: '600',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s ease',
                              opacity: saving ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!saving) {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow =
                                  '0 4px 8px rgba(220, 53, 69, 0.4)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            <FaTrash className="me-1" /> Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Modal
            show={showSubjectModal}
            onHide={() => setShowSubjectModal(false)}
            centered
          >
            <Modal.Header
              closeButton
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
              }}
            >
              <Modal.Title style={{ fontWeight: '600', fontSize: '1.25rem' }}>
                Select Subject
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '24px' }}>
              <Form.Group>
                <Form.Label
                  style={{
                    fontWeight: '600',
                    color: '#495057',
                    marginBottom: '10px',
                  }}
                >
                  Subject
                </Form.Label>
                <Form.Select
                  value={selectedSubjectId}
                  onChange={(e) => setSelectedSubjectId(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '2px solid #e9ecef',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow =
                      '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">-- Select Subject --</option>
                  {selectedExam?.subjects?.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer
              style={{
                background: '#f8f9fa',
                border: 'none',
                padding: '16px 24px',
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setShowSubjectModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!selectedSubjectId}
                onClick={() => {
                  setShowSubjectModal(false);
                  navigate(
                    `/exam-mark-entry?term_exam_id=${selectedExam.id}&subject_id=${selectedSubjectId}`,
                  );
                }}
                style={{
                  background: selectedSubjectId
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#ccc',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: selectedSubjectId ? 'pointer' : 'not-allowed',
                }}
              >
                Proceed to Mark Entry
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            size="lg"
            centered
          >
            <Modal.Header
              closeButton
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
              }}
            >
              <Modal.Title style={{ fontWeight: '600', fontSize: '1.25rem' }}>
                {editData ? (
                  <>
                    <FaEdit className="me-2" />
                    Edit Term Exam
                  </>
                ) : (
                  <>
                    <FaPlus className="me-2" />
                    Create Term Exam
                  </>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '24px' }}>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                    Term Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Mid Term Exam"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e9ecef',
                      fontSize: '0.95rem',
                      transition: 'all 0.3s ease',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow =
                        '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                    Batch
                  </Form.Label>
                  <Form.Select
                    value={formData.batch_id}
                    onChange={(e) =>
                      setFormData({ ...formData, batch_id: e.target.value })
                    }
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e9ecef',
                      fontSize: '0.95rem',
                      transition: 'all 0.3s ease',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow =
                        '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">-- Select Batch --</option>
                    {batches.map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.class_name} {batch.division}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                    Evaluation Type
                  </Form.Label>
                  <Form.Select
                    value={formData.evaluation_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        evaluation_type: e.target.value,
                      })
                    }
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e9ecef',
                      fontSize: '0.95rem',
                      transition: 'all 0.3s ease',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow =
                        '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="mark">Mark Based</option>
                    <option value="grade">Grade Based</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                    Subjects
                  </Form.Label>
                  <div
                    style={{
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      padding: '12px',
                      maxHeight: '250px',
                      overflowY: 'auto',
                      background: '#fafbfc',
                    }}
                  >
                    {subjects.map((subj) => (
                      <Form.Check
                        key={subj.id}
                        type="checkbox"
                        id={`subject-${subj.id}`}
                        label={subj.name}
                        checked={formData.subjects.includes(String(subj.id))}
                        onChange={(e) => {
                          const subjectId = String(subj.id);
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              subjects: [...formData.subjects, subjectId],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              subjects: formData.subjects.filter(
                                (id) => id !== subjectId,
                              ),
                            });
                          }
                        }}
                        style={{
                          padding: '12px 16px',
                          marginBottom: '10px',
                          background: formData.subjects.includes(
                            String(subj.id),
                          )
                            ? '#e7f3ff'
                            : '#fff',
                          borderRadius: '6px',
                          border: '1px solid',
                          borderColor: formData.subjects.includes(
                            String(subj.id),
                          )
                            ? '#667eea'
                            : '#dee2e6',
                          transition: 'all 0.2s ease',
                          fontSize: '1rem',
                        }}
                        className="custom-checkbox-large"
                      />
                    ))}
                  </div>
                  <Form.Text
                    className="text-muted"
                    style={{
                      fontSize: '0.85rem',
                      marginTop: '8px',
                      display: 'block',
                    }}
                  >
                    Select subjects for this exam. All subjects will use{' '}
                    <strong>
                      {formData.evaluation_type === 'mark'
                        ? 'mark-based'
                        : 'grade-based'}
                    </strong>{' '}
                    evaluation.
                  </Form.Text>
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer
              style={{
                background: '#f8f9fa',
                border: 'none',
                padding: '16px 24px',
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  background: saving
                    ? '#ccc'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>{editData ? 'Update' : 'Create'}</>
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Layout>
  );
};

export default TermExamsPage;
